/**
 * Delete confirmation dialog component.
 *
 * @module DeleteConfirmationDialog
 */

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Trash2, Loader2 } from 'lucide-react';
import { UserSubmission } from './types';

interface DeleteConfirmationDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly submissionToDelete: UserSubmission | null;
  readonly deleting: boolean;
  readonly onConfirm: () => void;
}

export function DeleteConfirmationDialog({
  open,
  onOpenChange,
  submissionToDelete,
  deleting,
  onConfirm,
}: Readonly<DeleteConfirmationDialogProps>) {
  const moduleLabels: Record<string, string> = {
    reading: 'Reading',
    listening: 'Listening',
    writing: 'Writing',
    speaking: 'Speaking',
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-ui">Delete Submission</DialogTitle>
          <DialogDescription className="font-ui">
            This action cannot be undone. This will permanently delete the
            submission and all associated data.
          </DialogDescription>
        </DialogHeader>

        {submissionToDelete && (
          <div className="space-y-3">
            <Alert>
              <AlertDescription className="font-ui">
                <div className="space-y-2">
                  <div>
                    <strong>User:</strong> {submissionToDelete.username}
                  </div>
                  <div>
                    <strong>Session ID:</strong> {submissionToDelete.session_id}
                  </div>
                  <div>
                    <strong>Exam Type:</strong> {submissionToDelete.exam_type}
                  </div>
                  <div>
                    <strong>Level:</strong> {submissionToDelete.level.join(', ')}
                  </div>
                  <div>
                    <strong>Date:</strong>{' '}
                    {new Date(submissionToDelete.created_at).toLocaleString()}
                  </div>
                  <div>
                    <strong>Modules:</strong>{' '}
                    {submissionToDelete.modules
                      .map((m) => moduleLabels[m.module] || m.module)
                      .join(', ')}
                  </div>
                </div>
              </AlertDescription>
            </Alert>

            <div className="flex justify-end gap-2 pt-4">
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={deleting}
              >
                <span className="font-ui">Cancel</span>
              </Button>
              <Button
                variant="destructive"
                onClick={onConfirm}
                disabled={deleting}
              >
                {deleting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    <span className="font-ui">Deleting...</span>
                  </>
                ) : (
                  <>
                    <Trash2 className="h-4 w-4 mr-2" />
                    <span className="font-ui">Delete Permanently</span>
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
